import { 
  Target,
  BookOpen,
  MousePointer,
  Clock,
  Image,
  Beaker,
  Hash,
  Grid3X3,
  Volume2,
  FileText,
  CheckCircle,
  Monitor,
  GitCompare,
  Layers,
  Compass,
  Repeat,
  PenTool,
  Move,
  Brain,
  ArrowUpDown
} from 'lucide-react';
// AVAILABLE_SHAPES, AVAILABLE_COLORS, and AVAILABLE_SIZES are no longer used
// since GPF resource types are now handled in getTranslatedResourceTypes function
export const resourceTypes = [
  {
    id: 'number_line',
    name: 'Number Line',
    description: 'Interactive number line for counting and number sense',
    icon: Target,
    defaultConfig: {
      type: 'number_line',
      min: 0,
      max: 20,
      tick_every: 1,
      interactive: true
    }
  },
  {
    id: 'ten_frame',
    name: 'Ten Frame',
    description: 'Interactive ten frame for number representation',
    icon: BookOpen,
    defaultConfig: {
      type: 'ten_frame',
      rows: 2,
      cols: 5,
      double_frame: true,
      drag_counters: true
    }
  },
  {
    id: 'count_objects_canvas',
    name: 'Count Objects',
    description: 'Interactive canvas for counting objects',
    icon: MousePointer,
    defaultConfig: {
      type: 'count_objects_canvas',
      max_objects: 20,
      randomize: true,
      touch_feedback: 'ping'
    }
  },
  {
    id: 'matching_objects',
    name: 'Matching Objects',
    description: 'Match different representations of quantities',
    icon: GitCompare,
    defaultConfig: {
      type: 'matching_objects',
      max_value: 10,
      item_count: 4,
      include_objects: true,
      include_numerals: true,
      include_ten_frames: true,
      include_words: false,
      object_layout: 'random_clustered'
    }
  },
  {
    id: 'timer',
    name: 'Timer',
    description: 'Countdown timer for activities',
    icon: Clock,
    defaultConfig: {
      type: 'timer',
      seconds: 60,
      autoLaunch: false
    }
  },
  {
    id: 'quiz',
    name: 'Quick Quiz',
    description: 'Interactive assessment tool',
    icon: Target,
    defaultConfig: {
      type: 'quiz',
      items: 5,
      shuffle: true,
      questions: [
        {
          id: 'q1',
          prompt: 'How many objects do you see?',
          answer: 8,
          choices: [6, 7, 8, 9, 10],
          resource_component: {
            type: 'count_objects_canvas',
            max_objects: 8,
            randomize: true,
            touch_feedback: 'ping'
          }
        },
        {
          id: 'q2',
          prompt: 'What number comes after 9?',
          answer: 10,
          choices: [8, 9, 10, 11, 12]
        }
      ]
    }
  },
  {
    id: 'interactive_diagram',
    name: 'Interactive Diagram',
    description: 'Interactive diagram with clickable stages',
    icon: Image,
    defaultConfig: {
      type: 'interactive_diagram',
      stages: ['stage1', 'stage2', 'stage3'],
      interactive: true,
      stage_data: {
        stage1: {
          title: 'Stage 1',
          description: 'First stage description',
          details: 'Detailed information about stage 1',
          icon: '🌱'
        },
        stage2: {
          title: 'Stage 2',
          description: 'Second stage description',
          details: 'Detailed information about stage 2',
          icon: '🌿'
        },
        stage3: {
          title: 'Stage 3',
          description: 'Third stage description',
          details: 'Detailed information about stage 3',
          icon: '🌳'
        }
      }
    }
  },
  {
    id: 'simulation',
    name: 'Virtual Lab Simulation',
    description: 'Interactive science experiment simulation',
    icon: Beaker,
    defaultConfig: {
      type: 'simulation',
      components: ['water', 'heat', 'condensation_chamber'],
      interactive: true,
      component_info: {
        water: {
          icon: '💧',
          name: 'Water',
          description: 'Liquid water for the experiment'
        },
        heat: {
          icon: '🔥',
          name: 'Heat Source',
          description: 'Heat to evaporate water'
        },
        condensation_chamber: {
          icon: '❄️',
          name: 'Condensation Chamber',
          description: 'Cool area for water to condense'
        }
      },
      simulation_data: {
        title: 'Mini Water Cycle Experiment',
        description: 'Observe how water changes states through evaporation and condensation',
        initial_conditions: {
          temperature: 25,
          humidity: 60,
          water_level: 100
        },
        steps: [
          {
            step: 0,
            name: 'Initial Setup',
            description: 'Water at room temperature',
            conditions: {
              temperature: 25,
              humidity: 60,
              water_level: 100
            },
            status: 'Ready to start experiment'
          },
          {
            step: 1,
            name: 'Heating Phase',
            description: 'Heat source warms the water',
            conditions: {
              temperature: 85,
              humidity: 75,
              water_level: 85
            },
            status: 'Heating water... Water beginning to evaporate'
          },
          {
            step: 2,
            name: 'Condensation Phase',
            description: 'Water vapor cools and condenses',
            conditions: {
              temperature: 45,
              humidity: 95,
              water_level: 70
            },
            status: 'Water vapor condensing on cool surface'
          }
        ],
        learning_objectives: [
          'Understand the three states of water (solid, liquid, gas)',
          'Observe the process of evaporation',
          'See how condensation forms water droplets',
          'Connect to the natural water cycle'
        ],
        safety_notes: [
          'This is a virtual experiment - no real heat sources used',
          'In real experiments, always use proper safety equipment',
          'Adult supervision required for real heat experiments'
        ]
      }
    }
  },
  {
    id: 'flashcard_set',
    name: 'Flashcard Set',
    description: 'Interactive flashcards for learning',
    icon: Hash,
    defaultConfig: {
      type: 'flashcard_set',
      items: [
        { front: '0', back: 'Zero' },
        { front: '1', back: 'One' },
        { front: '2', back: 'Two' },
        { front: '3', back: 'Three' },
        { front: '4', back: 'Four' },
        { front: '5', back: 'Five' },
        { front: '6', back: 'Six' },
        { front: '7', back: 'Seven' },
        { front: '8', back: 'Eight' },
        { front: '9', back: 'Nine' },
        { front: '10', back: 'Ten' }
      ],
      randomize: true,
      auto_advance_seconds: 3
    }
  },
  {
    id: 'grid_activity',
    name: 'Grid Activity',
    description: 'Interactive grid-based learning activity',
    icon: Grid3X3,
    defaultConfig: {
      type: 'grid_activity',
      rows: 4,
      cols: 6,
      bank: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      mode: 'read_and_select'
    }
  },
  {
    id: 'audio_bank',
    name: 'Audio Bank',
    description: 'Audio resources for auditory learning',
    icon: Volume2,
    defaultConfig: {
      type: 'audio_bank',
      words: ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
      auto_play: false,
      loop: false
    }
  },
  {
    id: 'memory_game',
    name: 'Memory Game',
    description: 'Interactive memory game to match emojis with words',
    icon: Brain,
    defaultConfig: {
      type: 'memory_game',
      title: 'Memory Game',
      description: 'Match emojis with their corresponding words',
      items: [
        { front: '🐔', back: 'poule' },
        { front: '🐑', back: 'mouton' },
        { front: '🐦', back: 'canari' },
        { front: '🥄', back: 'pilon' },
        { front: '💡', back: 'lampe' },
        { front: '🏠', back: 'case' },
        { front: '👒', back: 'chapeau' }
      ],
      autoStart: false,
      showHints: true,
      soundEffects: false,
      difficulty: 'easy'
    }
  },
  {
    id: 'sorting_game',
    name: 'Sorting Game',
    description: 'Interactive sorting game to categorize items',
    icon: ArrowUpDown,
    defaultConfig: {
      type: 'sorting_game',
      title: 'Sorting Game',
      description: 'Sort items into categories',
      items: [
        { emoji: '🐔', name: 'poule', category: 'animaux' },
        { emoji: '🐑', name: 'mouton', category: 'animaux' },
        { emoji: '🐦', name: 'canari', category: 'animaux' },
        { emoji: '🥄', name: 'pilon', category: 'objets' },
        { emoji: '💡', name: 'lampe', category: 'objets' },
        { emoji: '🏠', name: 'case', category: 'objets' },
        { emoji: '👒', name: 'chapeau', category: 'objets' }
      ],
      categories: [
        { id: 'animaux', name: 'Animaux', color: '#EF4444', emoji: '🐾' },
        { id: 'objets', name: 'Objets', color: '#3B82F6', emoji: '📦' }
      ],
      autoStart: false,
      showHints: true
    }
  },
  // GPF Resource Types are now handled in getTranslatedResourceTypes function




];

export const getTranslatedResourceTypes = (t: (key: string) => string) => {
  return [
    {
      id: 'number_line',
      name: t('resourceTypes.numberLine'),
      description: t('resourceTypes.numberLineDescription'),
      icon: Target,
      defaultConfig: {
        type: 'number_line',
        min: 0,
        max: 20,
        tick_every: 1,
        interactive: true
      }
    },
    {
      id: 'ten_frame',
      name: t('resourceTypes.tenFrame'),
      description: t('resourceTypes.tenFrameDescription'),
      icon: BookOpen,
      defaultConfig: {
        type: 'ten_frame',
        rows: 2,
        cols: 5,
        double_frame: true,
        drag_counters: true
      }
    },
    {
      id: 'count_objects_canvas',
      name: t('resourceTypes.countObjects'),
      description: t('resourceTypes.countObjectsDescription'),
      icon: MousePointer,
      defaultConfig: {
        type: 'count_objects_canvas',
        max_objects: 20,
        randomize: true,
        touch_feedback: 'ping'
      }
    },
    {
      id: 'matching_objects',
      name: t('resourceTypes.matchingObjects'),
      description: t('resourceTypes.matchingObjectsDescription'),
      icon: GitCompare,
      defaultConfig: {
        type: 'matching_objects',
        max_value: 10,
        item_count: 4,
        include_objects: true,
        include_numerals: true,
        include_ten_frames: true,
        include_words: false,
        object_layout: 'random_clustered'
      }
    },
    {
      id: 'timer',
      name: t('resourceTypes.timer'),
      description: t('resourceTypes.timerDescription'),
      icon: Clock,
      defaultConfig: {
        type: 'timer',
        seconds: 60,
        autoLaunch: false
      }
    },
    {
      id: 'quiz',
      name: t('resourceTypes.quiz'),
      description: t('resourceTypes.quizDescription'),
      icon: Target,
      defaultConfig: {
        type: 'quiz',
        items: 5,
        shuffle: true,
        questions: [
          {
            id: 'q1',
            prompt: t('resourceTypes.sampleQuestion1'),
            answer: 8,
            choices: [6, 7, 8, 9, 10],
            resource_component: {
              type: 'count_objects_canvas',
              max_objects: 8,
              randomize: true,
              touch_feedback: 'ping'
            }
          },
          {
            id: 'q2',
            prompt: t('resourceTypes.sampleQuestion2'),
            answer: 10,
            choices: [8, 9, 10, 11, 12]
          }
        ]
      }
    },
    {
      id: 'image',
      name: t('resourceTypes.image'),
      description: t('resourceTypes.imageDescription'),
      icon: Image,
      defaultConfig: {
        type: 'image',
        url: '',
        alt: '',
        alignment: 'center',
        fit: 'contain'
      }
    },
    {
      id: 'rich_text',
      name: t('resourceTypes.richText'),
      description: t('resourceTypes.richTextDescription'),
      icon: FileText,
      defaultConfig: {
        type: 'rich_text',
        content: '',
        title: '',
        alignment: 'left',
        fontSize: 'inherit',
        backgroundColor: 'white',
        textColor: 'inherit'
      }
    },
    {
      id: 'interactive_diagram',
      name: t('resourceTypes.interactiveDiagram'),
      description: t('resourceTypes.interactiveDiagramDescription'),
      icon: Image,
      defaultConfig: {
        type: 'interactive_diagram',
        stages: ['stage1', 'stage2', 'stage3'],
        interactive: true,
        stage_data: {
          stage1: {
            title: t('resourceTypes.stage1'),
            description: t('resourceTypes.stage1Description'),
            details: t('resourceTypes.stage1Details'),
            icon: '🌱'
          },
          stage2: {
            title: t('resourceTypes.stage2'),
            description: t('resourceTypes.stage2Description'),
            details: t('resourceTypes.stage2Details'),
            icon: '🌿'
          },
          stage3: {
            title: t('resourceTypes.stage3'),
            description: t('resourceTypes.stage3Description'),
            details: t('resourceTypes.stage3Details'),
            icon: '🌳'
          }
        }
      }
    },
    {
      id: 'simulation',
      name: t('resourceTypes.simulation'),
      description: t('resourceTypes.simulationDescription'),
      icon: Beaker,
      defaultConfig: {
        type: 'simulation',
        components: ['water', 'heat', 'condensation_chamber'],
        interactive: true,
        component_info: {
          water: {
            icon: '💧',
            name: t('resourceTypes.water'),
            description: t('resourceTypes.waterDescription')
          },
          heat: {
            icon: '🔥',
            name: t('resourceTypes.heat'),
            description: t('resourceTypes.heatDescription')
          },
          condensation_chamber: {
            icon: '❄️',
            name: t('resourceTypes.condensationChamber'),
            description: t('resourceTypes.condensationChamberDescription')
          }
        },
        simulation_data: {
          title: t('resourceTypes.simulationTitle'),
          description: t('resourceTypes.simulationDataDescription'),
          initial_conditions: {
            temperature: 25,
            humidity: 60,
            water_level: 100
          },
          steps: [
            {
              step: 0,
              name: t('resourceTypes.initialSetup'),
              description: t('resourceTypes.initialSetupDescription'),
              conditions: {
                temperature: 25,
                humidity: 60,
                water_level: 100
              },
              status: t('resourceTypes.readyToStart')
            },
            {
              step: 1,
              name: t('resourceTypes.heatingPhase'),
              description: t('resourceTypes.heatingPhaseDescription'),
              conditions: {
                temperature: 85,
                humidity: 75,
                water_level: 85
              },
              status: t('resourceTypes.heatingWater')
            },
            {
              step: 2,
              name: t('resourceTypes.condensationPhase'),
              description: t('resourceTypes.condensationPhaseDescription'),
              conditions: {
                temperature: 45,
                humidity: 95,
                water_level: 70
              },
              status: t('resourceTypes.waterVaporCondensing')
            }
          ],
          learning_objectives: [
            t('resourceTypes.learningObjective1'),
            t('resourceTypes.learningObjective2'),
            t('resourceTypes.learningObjective3'),
            t('resourceTypes.learningObjective4')
          ],
          safety_notes: [
            t('resourceTypes.safetyNote1'),
            t('resourceTypes.safetyNote2'),
            t('resourceTypes.safetyNote3')
          ]
        }
      }
    },
    {
      id: 'flashcard_set',
      name: t('resourceTypes.flashcardSet'),
      description: t('resourceTypes.flashcardSetDescription'),
      icon: Hash,
      defaultConfig: {
        type: 'flashcard_set',
        items: [
          { front: '0', back: t('resourceTypes.zero') },
          { front: '1', back: t('resourceTypes.one') },
          { front: '2', back: t('resourceTypes.two') },
          { front: '3', back: t('resourceTypes.three') },
          { front: '4', back: t('resourceTypes.four') },
          { front: '5', back: t('resourceTypes.five') },
          { front: '6', back: t('resourceTypes.six') },
          { front: '7', back: t('resourceTypes.seven') },
          { front: '8', back: t('resourceTypes.eight') },
          { front: '9', back: t('resourceTypes.nine') },
          { front: '10', back: t('resourceTypes.ten') }
        ],
        randomize: true,
        auto_advance_seconds: 3
      }
    },
    {
      id: 'grid_activity',
      name: t('resourceTypes.gridActivity'),
      description: t('resourceTypes.gridActivityDescription'),
      icon: Grid3X3,
      defaultConfig: {
        type: 'grid_activity',
        rows: 4,
        cols: 6,
        bank: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
        mode: 'read_and_select'
      }
    },
    {
      id: 'audio_bank',
      name: t('resourceTypes.audioBank'),
      description: t('resourceTypes.audioBankDescription'),
      icon: Volume2,
      defaultConfig: {
        type: 'audio_bank',
        words: [t('resourceTypes.zero'), t('resourceTypes.one'), t('resourceTypes.two'), t('resourceTypes.three'), t('resourceTypes.four'), t('resourceTypes.five'), t('resourceTypes.six'), t('resourceTypes.seven'), t('resourceTypes.eight'), t('resourceTypes.nine'), t('resourceTypes.ten')],
        auto_play: false,
        loop: false
      }
    },
    {
      id: 'memory_game',
      name: t('resourceTypes.memoryGame'),
      description: t('resourceTypes.memoryGameDescription'),
      icon: Brain,
      configForm: 'MemoryGameConfigurationForm',
      defaultConfig: {
        type: 'memory_game',
        title: t('resourceTypes.memoryGame'),
        description: t('resourceTypes.memoryGameDescription'),
        items: [
          { front: '🐔', back: 'poule' },
          { front: '🐑', back: 'mouton' },
          { front: '🐦', back: 'canari' },
          { front: '🥄', back: 'pilon' },
          { front: '💡', back: 'lampe' },
          { front: '🏠', back: 'case' },
          { front: '👒', back: 'chapeau' }
        ],
        autoStart: false,
        showHints: true,
        soundEffects: false,
        difficulty: 'easy'
      }
    },
    {
      id: 'sorting_game',
      name: t('resourceTypes.sortingGame'),
      description: t('resourceTypes.sortingGameDescription'),
      icon: ArrowUpDown,
      configForm: 'SortingGameConfigurationForm',
      defaultConfig: {
        type: 'sorting_game',
        title: t('resourceTypes.sortingGame'),
        description: t('resourceTypes.sortingGameDescription'),
        items: [
          { emoji: '🐔', name: 'poule', category: 'animaux' },
          { emoji: '🐑', name: 'mouton', category: 'animaux' },
          { emoji: '🐦', name: 'canari', category: 'animaux' },
          { emoji: '🥄', name: 'pilon', category: 'objets' },
          { emoji: '💡', name: 'lampe', category: 'objets' },
          { emoji: '🏠', name: 'case', category: 'objets' },
          { emoji: '👒', name: 'chapeau', category: 'objets' }
        ],
        categories: [
          { id: 'animaux', name: 'Animaux', color: '#EF4444', emoji: '🐾' },
          { id: 'objets', name: 'Objets', color: '#3B82F6', emoji: '📦' }
        ],
        autoStart: false,
        showHints: true
      }
    },
    // GPF Resource Types
    {
      id: 'sorting_canvas',
      name: t('resourceTypes.sortingCanvas'),
      description: t('resourceTypes.sortingCanvasDescription'),
      icon: Layers,
      configForm: 'SortingCanvasConfigForm',
      defaultConfig: {
        type: 'sorting_canvas',
        objects: [
          { type: 'circle', count: 5, color: 'red' },
          { type: 'square', count: 5, color: 'blue' },
          { type: 'triangle', count: 5, color: 'green' }
        ],
        categories: [
          { id: 'couleur', name: 'couleur', color: '#EF4444', sortingCriteria: 'color', availableValues: ['red', 'blue', 'green'] },
          { id: 'forme', name: 'forme', color: '#3B82F6', sortingCriteria: 'shape', availableValues: ['circle', 'square', 'triangle'] },
          { id: 'taille', name: 'taille', color: '#10B981', sortingCriteria: 'size', availableValues: ['small', 'medium', 'large'] }
        ],
        showCount: true,
        allowInteraction: true
      }
    },
    {
      id: 'spatial_canvas',
      name: t('resourceTypes.spatialCanvas'),
      description: t('resourceTypes.spatialCanvasDescription'),
      icon: Compass,
      configForm: 'SpatialCanvasConfigForm',
      defaultConfig: {
        type: 'spatial_canvas',
        gridSize: '5x5',
        objects: [
          { id: 'star', type: 'star', position: 'center', color: 'yellow' },
          { id: 'circle', type: 'circle', position: 'left', color: 'red' },
          { id: 'square', type: 'square', position: 'top', color: 'blue' },
          { id: 'triangle', type: 'triangle', position: 'right', color: 'green' }
        ],
        directions: ['devant', 'derrière', 'gauche', 'droite', 'haut', 'bas'],
        showGrid: true,
        allowInteraction: true
      }
    },
    {
      id: 'topology_canvas',
      name: t('resourceTypes.topologyCanvas'),
      description: t('resourceTypes.topologyCanvasDescription'),
      icon: Target,
      configForm: 'TopologyCanvasConfigurationForm',
      defaultConfig: {
        type: 'topology_canvas',
        question: 'Placez les objets dedans ou dehors du cercle',
        zones: [
          {
            id: 'zone1',
            type: 'circle',
            x: 300,
            y: 200,
            radius: 120,
            color: '#3B82F6',
            label: 'Zone délimitée'
          }
        ],
        objects: [
          { id: 'ballon', type: 'circle', position: 'dedans', color: 'red', emoji: '⚽' },
          { id: 'livre', type: 'square', position: 'dehors', color: 'blue', emoji: '📚' },
          { id: 'pomme', type: 'circle', position: 'dedans', color: 'red', emoji: '🍎' },
          { id: 'voiture', type: 'square', position: 'dehors', color: 'blue', emoji: '🚗' }
        ],
        allowInteraction: true
      }
    },
    {
      id: 'pattern_maker',
      name: t('resourceTypes.patternMaker'),
      description: t('resourceTypes.patternMakerDescription'),
      icon: Repeat,
      configForm: 'PatternMakerConfigurationForm',
      defaultConfig: {
        type: 'pattern_maker',
        name: 'Créateur de Rythmes',
        question: 'Reproduisez le rythme affiché en utilisant les éléments disponibles',
        pattern: [
          { id: 'p1', emoji: '🔴', color: '#EF4444', type: 'shape' },
          { id: 'p2', emoji: '🔵', color: '#3B82F6', type: 'shape' },
          { id: 'p3', emoji: '🔴', color: '#EF4444', type: 'shape' },
          { id: 'p4', emoji: '🔵', color: '#3B82F6', type: 'shape' }
        ],
        options: [
          { id: 'o1', emoji: '🔴', color: '#EF4444', type: 'shape' },
          { id: 'o2', emoji: '🔵', color: '#3B82F6', type: 'shape' },
          { id: 'o3', emoji: '🟡', color: '#F59E0B', type: 'shape' },
          { id: 'o4', emoji: '🟢', color: '#10B981', type: 'shape' }
        ],
        maxLength: 8,
        showHint: true,
        allowInteraction: true
      }
    },
    {
      id: 'drawing_board',
      name: t('resourceTypes.drawingBoard'),
      description: t('resourceTypes.drawingBoardDescription'),
      icon: PenTool,
      configForm: 'DrawingBoardConfigForm',
      defaultConfig: {
        type: 'drawing_board',
        gridSize: '10x10',
        tools: ['crayon', 'gomme', 'règle', 'formes'],
        templates: [],
        allowInteraction: true
      }
    },
    {
      id: 'object_manipulator',
      name: t('resourceTypes.objectManipulator'),
      description: t('resourceTypes.objectManipulatorDescription'),
      icon: Move,
      configForm: 'ObjectManipulatorConfigForm',
      defaultConfig: {
        type: 'object_manipulator',
        objects: [
          { 
            id: 'obj1', 
            type: 'circle', 
            color: 'red',
            size: 'medium',
            initialPosition: { x: 50, y: 50 },
            initialRotation: 0,
            initialScale: 1
          },
          { 
            id: 'obj2', 
            type: 'square', 
            color: 'blue',
            size: 'medium',
            initialPosition: { x: 150, y: 50 },
            initialRotation: 0,
            initialScale: 1
          },
          { 
            id: 'obj3', 
            type: 'triangle', 
            color: 'green',
            size: 'medium',
            initialPosition: { x: 250, y: 50 },
            initialRotation: 0,
            initialScale: 1
          }
        ],
        transformations: [
          { 
            type: 'move', 
            target: 'obj1',
            instruction: 'Déplacez l\'objet vers la position demandée'
          },
          { 
            type: 'rotate', 
            target: 'obj2',
            instruction: 'Faites pivoter l\'objet selon l\'instruction'
          },
          { 
            type: 'scale', 
            target: 'obj3',
            instruction: 'Redimensionnez l\'objet selon l\'instruction'
          }
        ],
        allowInteraction: true
      }
    },
    {
      id: 'shape_recognition',
      name: t('resourceTypes.shapeRecognition'),
      description: t('resourceTypes.shapeRecognitionDescription'),
      icon: Target,
      configForm: 'ShapeRecognitionConfigurationForm',
      defaultConfig: {
        type: 'shape_recognition',
        name: 'Reconnaissance des Formes',
        instructions: 'Reconnaissez et nommez les formes géométriques.',
        shapes: [
          {
            id: 'shape1',
            emoji: '🔴',
            name: 'Cercle',
            description: 'Une forme ronde sans angles',
            properties: ['Aucun côté droit', 'Entièrement courbe', 'Symétrique']
          },
          {
            id: 'shape2',
            emoji: '🔷',
            name: 'Carré',
            description: 'Une forme avec 4 côtés égaux',
            properties: ['4 côtés égaux', '4 angles droits', 'Symétrique']
          },
          {
            id: 'shape3',
            emoji: '🔺',
            name: 'Triangle',
            description: 'Une forme avec 3 côtés',
            properties: ['3 côtés', '3 angles', 'Peut être équilatéral']
          },
          {
            id: 'shape4',
            emoji: '📐',
            name: 'Rectangle',
            description: 'Une forme avec 4 côtés opposés égaux',
            properties: ['4 côtés', 'Côtés opposés égaux', '4 angles droits']
          }
        ],
        showHints: true,
        allowCorrections: true,
        difficultyLevel: 'easy'
      }
    },
    {
      id: 'size_comparison',
      name: 'Comparaison de tailles',
      description: 'Activité interactive pour comparer et classer des objets par taille',
      icon: Target,
      configForm: 'SizeComparisonConfigurationForm',
      defaultConfig: {
        type: 'size_comparison',
        name: 'Comparaison de Tailles',
        title: 'Comparaison de Tailles',
        description: 'Comparez les objets et classez-les par taille',
        objects: [
          { id: 'obj1', name: 'Crayon court', size: 'small', length: 8, color: 'red', emoji: '✏️' },
          { id: 'obj2', name: 'Crayon long', size: 'large', length: 15, color: 'blue', emoji: '✏️' },
          { id: 'obj3', name: 'Gomme', size: 'small', length: 3, color: 'pink', emoji: '🧽' }
        ],
        instructions: 'Glissez les objets pour les ranger du plus petit au plus grand',
        showMeasurements: true,
        allowDrag: true
      }
    },
    {
      id: 'addition_manipulator',
      name: 'Manipulateur d\'additions',
      description: 'Outil interactif pour manipuler des objets et comprendre les additions',
      icon: Hash,
      configForm: 'AdditionManipulatorConfigurationForm',
      defaultConfig: {
        type: 'addition_manipulator',
        name: 'Manipulateur d\'Additions',
        title: 'Manipulateur d\'Additions',
        description: 'Manipulez des objets pour comprendre les additions',
        maxNumber: 10,
        objects: [
          { id: 'obj1', emoji: '🍎', name: 'Pomme', color: 'red' },
          { id: 'obj2', emoji: '🍌', name: 'Banane', color: 'yellow' },
          { id: 'obj3', emoji: '🍊', name: 'Orange', color: 'orange' }
        ],
        problems: [
          {
            id: 'p1',
            question: 'Combien y a-t-il de fruits en tout ?',
            leftGroup: 3,
            rightGroup: 2,
            answer: 5
          }
        ],
        instructions: 'Glissez les objets dans les groupes pour résoudre les additions',
        showAnswer: true,
        allowDrag: true
      }
    }

  ];
};

export const getTranslatedUIComponentTypes = (t: (key: string) => string) => {
  return [
    {
      type: 'display',
      name: t('uiComponentTypes.display'),
      description: t('uiComponentTypes.displayDescription'),
      icon: Monitor
    },
    {
      type: 'manipulative',
      name: t('uiComponentTypes.manipulative'),
      description: t('uiComponentTypes.manipulativeDescription'),
      icon: MousePointer
    },
    {
      type: 'timer',
      name: t('uiComponentTypes.timer'),
      description: t('uiComponentTypes.timerDescription'),
      icon: Clock
    },
    {
      type: 'quiz',
      name: t('uiComponentTypes.quiz'),
      description: t('uiComponentTypes.quizDescription'),
      icon: Target
    },
    {
      type: 'activity',
      name: t('uiComponentTypes.activity'),
      description: t('uiComponentTypes.activityDescription'),
      icon: Target
    },
    {
      type: 'assessment',
      name: t('uiComponentTypes.assessment'),
      description: t('uiComponentTypes.assessmentDescription'),
      icon: FileText
    },
    {
      type: 'feedback',
      name: t('uiComponentTypes.feedback'),
      description: t('uiComponentTypes.feedbackDescription'),
      icon: CheckCircle
    }
  ];
};

// Keep the original for backward compatibility
export const uiComponentTypes = [
  {
    type: 'display',
    name: 'Display',
    description: 'Show a resource for viewing only',
    icon: Monitor
  },
  {
    type: 'manipulative',
    name: 'Manipulative',
    description: 'Interactive resource for hands-on learning',
    icon: MousePointer
  },
  {
    type: 'timer',
    name: 'Timer',
    description: 'Countdown timer component',
    icon: Clock
  },
  {
    type: 'quiz',
    name: 'Quiz',
    description: 'Assessment component',
    icon: Target
  },
  {
    type: 'activity',
    name: 'Activity',
    description: 'Interactive learning activity',
    icon: Target
  },
  {
    type: 'assessment',
    name: 'Assessment',
    description: 'Student assessment component',
    icon: FileText
  },
  {
    type: 'feedback',
    name: 'Feedback',
    description: 'Student feedback component',
    icon: CheckCircle
  },
  {
    id: "evaluation_dashboard",
    name: "Tableau de Bord d'Évaluation",
    description: "Suivi de progression pour l'évaluation des compétences",
    icon: "BarChart3",
    configForm: "EvaluationDashboardConfigurationForm",
    defaultConfig: {
      type: "evaluation_dashboard",
      name: "Tableau de Bord d'Évaluation",
      title: "Tableau de Bord d'Évaluation",
      description: "Suivez votre progression dans l'évaluation",
      competencies: [
        {
          id: "sorting",
          name: "Tri et Classification",
          description: "Classer des objets selon différents critères",
          weight: 20,
          activities: ["Tri par couleur", "Tri par forme", "Tri par taille"]
        },
        {
          id: "spatial",
          name: "Concepts Spatiaux",
          description: "Comprendre les relations spatiales",
          weight: 15,
          activities: ["Dedans/Dehors", "Position relative", "Orientation"]
        },
        {
          id: "geometry",
          name: "Géométrie",
          description: "Reconnaître et identifier des formes",
          weight: 20,
          activities: ["Reconnaissance de formes", "Identification de propriétés", "Classification géométrique"]
        },
        {
          id: "measurement",
          name: "Mesure",
          description: "Comparer et mesurer des objets",
          weight: 15,
          activities: ["Comparaison de tailles", "Classement par longueur", "Estimation"]
        },
        {
          id: "logic",
          name: "Logique",
          description: "Comprendre les patterns et suites",
          weight: 15,
          activities: ["Reconnaissance de patterns", "Complétion de suites", "Création de patterns"]
        },
        {
          id: "calculation",
          name: "Calcul",
          description: "Effectuer des additions simples",
          weight: 15,
          activities: ["Additions jusqu'à 10", "Résolution de problèmes", "Manipulation d'objets"]
        }
      ],
      showProgress: true,
      allowRetry: true
    }
  },
  {
    id: "project_workspace",
    name: "Espace de Projet",
    description: "Espace de travail collaboratif pour les projets",
    icon: "Layout",
    configForm: "ProjectWorkspaceConfigurationForm",
    defaultConfig: {
      type: "project_workspace",
      name: "Espace de Projet",
      title: "Espace de Projet",
      description: "Créez votre projet en appliquant vos compétences",
      project_theme: "Jardin Mathématique",
      phases: [
        {
          id: "planning",
          name: "Planification",
          description: "Organisez votre projet",
          duration_minutes: 10,
          skills: ["sorting", "geometry"],
          tasks: ["Planifier l'organisation", "Choisir les éléments"]
        }
      ],
      collaboration_enabled: true,
      peer_review_enabled: true
    }
  },
  {
    id: "math_toolbox",
    name: "Boîte à Outils Mathématiques",
    description: "Collection d'outils mathématiques pour les projets",
    icon: "Tool",
    configForm: "MathToolboxConfigurationForm",
    defaultConfig: {
      type: "math_toolbox",
      name: "Boîte à Outils Mathématiques",
      title: "Boîte à Outils Mathématiques",
      description: "Tous les outils mathématiques pour votre projet",
      tools: [
        {
          id: "shape_creator",
          name: "Créateur de Formes",
          description: "Créez des formes géométriques",
          type: "geometry",
          icon: "Shapes"
        }
      ],
      show_tool_usage: true
    }
  },
  {
    id: "collaboration_board",
    name: "Tableau de Collaboration",
    description: "Plateforme de collaboration pour les projets d'équipe",
    icon: "Users",
    configForm: "CollaborationBoardConfigurationForm",
    defaultConfig: {
      type: "collaboration_board",
      name: "Tableau de Collaboration",
      title: "Tableau de Collaboration",
      description: "Collaborez avec vos pairs sur le projet",
      features: ["Chat en temps réel", "Partage d'idées", "Commentaires"],
      max_participants: 4,
      moderation_enabled: true
    }
  }
];
